import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Avatar,
  Badge,
  Paper,
  ThemeIcon,
  ActionIcon,
  Menu,
  Tooltip,
} from '@mantine/core';
import {
  IconMail,
  IconPhone,
  IconCalendar,
  IconNote,
  IconMessage,
  IconFile,
  IconCheck,
  IconClock,
  IconDots,
  IconEdit,
  IconTrash,
  IconExternalLink,
} from '@tabler/icons-react';
import { Card, Button } from '../../ui';
import { FadeIn, SlideIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface Activity {
  id: string;
  type: 'email' | 'call' | 'meeting' | 'note' | 'message' | 'file' | 'task' | 'custom';
  title: string;
  description?: string;
  timestamp: Date;
  user?: {
    id: string;
    name: string;
    avatar?: string;
  };
  contact?: {
    id: string;
    name: string;
  };
  deal?: {
    id: string;
    title: string;
  };
  status?: 'completed' | 'pending' | 'cancelled';
  duration?: number; // in minutes
  outcome?: string;
  attachments?: { name: string; url: string }[];
  metadata?: Record<string, unknown>;
  icon?: ReactNode;
  color?: string;
}

export interface ActivityTimelineProps {
  activities: Activity[];
  onActivityClick?: (activity: Activity) => void;
  onActivityEdit?: (activity: Activity) => void;
  onActivityDelete?: (activity: Activity) => void;
  onAddActivity?: (type: Activity['type']) => void;
  groupByDate?: boolean;
  showActions?: boolean;
  maxItems?: number;
  emptyMessage?: string;
  className?: string;
}

export const ActivityTimeline = forwardRef<HTMLDivElement, ActivityTimelineProps>(
  (
    {
      activities,
      onActivityClick,
      onActivityEdit,
      onActivityDelete,
      onAddActivity,
      groupByDate = true,
      showActions = true,
      maxItems,
      emptyMessage = 'No activities yet',
      className,
    },
    ref
  ) => {
    const displayActivities = maxItems ? activities.slice(0, maxItems) : activities;
    const locale = usePreferencesStore((state) => state.locale);
    const timezone = usePreferencesStore((state) => state.timezone);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const getTypeIcon = (type: Activity['type'], custom?: ReactNode) => {
      if (custom) return custom;

      switch (type) {
        case 'email':
          return <IconMail size={16} />;
        case 'call':
          return <IconPhone size={16} />;
        case 'meeting':
          return <IconCalendar size={16} />;
        case 'note':
          return <IconNote size={16} />;
        case 'message':
          return <IconMessage size={16} />;
        case 'file':
          return <IconFile size={16} />;
        case 'task':
          return <IconCheck size={16} />;
        default:
          return <IconClock size={16} />;
      }
    };

    const getTypeColor = (type: Activity['type'], custom?: string) => {
      if (custom) return custom;

      switch (type) {
        case 'email':
          return 'blue';
        case 'call':
          return 'green';
        case 'meeting':
          return 'violet';
        case 'note':
          return 'yellow';
        case 'message':
          return 'cyan';
        case 'file':
          return 'orange';
        case 'task':
          return 'teal';
        default:
          return 'gray';
      }
    };

    const getStatusColor = (status?: Activity['status']) => {
      switch (status) {
        case 'completed':
          return 'green';
        case 'pending':
          return 'yellow';
        case 'cancelled':
          return 'gray';
        default:
          return 'blue';
      }
    };

    const formatDate = (date: Date) => {
      const d = new Date(date);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (d.toDateString() === today.toDateString()) {
        return 'Today';
      }
      if (d.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }
      return d.toLocaleDateString(locale || undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: timezone,
      });
    };

    const formatTime = (date: Date) => {
      return new Date(date).toLocaleTimeString(locale || undefined, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timezone,
      });
    };

    const formatDuration = (minutes?: number) => {
      if (!minutes) return '';
      if (minutes < 60) return `${minutes}m`;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    // Group activities by date
    const groupedActivities = groupByDate
      ? displayActivities.reduce((groups, activity) => {
          const dateKey = new Date(activity.timestamp).toDateString();
          if (!groups[dateKey]) {
            groups[dateKey] = [];
          }
          groups[dateKey].push(activity);
          return groups;
        }, {} as Record<string, Activity[]>)
      : { all: displayActivities };

    const renderActivity = (activity: Activity, index: number) => (
      <SlideIn direction="left" delay={index * 30}>
        <Group gap="md" align="flex-start">
          {/* Timeline dot */}
          <Stack gap={0} align="center">
            <ThemeIcon
              size={32}
              radius="xl"
              color={getTypeColor(activity.type, activity.color)}
              variant="light"
            >
              {getTypeIcon(activity.type, activity.icon)}
            </ThemeIcon>
            <Box
              style={{
                width: 2,
                flex: 1,
                minHeight: 20,
                backgroundColor: 'var(--mantine-color-gray-3)',
              }}
            />
          </Stack>

          {/* Activity content */}
          <Paper
            p="sm"
            withBorder
            style={{
              flex: 1,
              cursor: onActivityClick ? 'pointer' : 'default',
            }}
            onClick={() => onActivityClick?.(activity)}
          >
            <Stack gap="xs">
              <Group justify="space-between">
                <Group gap="sm">
                  <Text size="sm" fw={500} style={accessibilityStyles}>{activity.title}</Text>
                  {activity.status && (
                    <Badge size="xs" color={getStatusColor(activity.status)}>
                      {activity.status}
                    </Badge>
                  )}
                </Group>
                <Group gap="xs">
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>{formatTime(activity.timestamp)}</Text>
                  {activity.duration && (
                    <Badge size="xs" variant="light">
                      {formatDuration(activity.duration)}
                    </Badge>
                  )}
                  {showActions && (onActivityEdit || onActivityDelete) && (
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDots size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {onActivityEdit && (
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            onClick={() => onActivityEdit(activity)}
                          >
                            Edit
                          </Menu.Item>
                        )}
                        {onActivityDelete && (
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => onActivityDelete(activity)}
                          >
                            Delete
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Group>

              {activity.description && (
                <Text size="sm" c="dimmed" lineClamp={2} style={accessibilityStyles}>
                  {activity.description}
                </Text>
              )}

              {/* Related entities */}
              <Group gap="md">
                {activity.contact && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    Contact: <Text span fw={500}>{activity.contact.name}</Text>
                  </Text>
                )}
                {activity.deal && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    Deal: <Text span fw={500}>{activity.deal.title}</Text>
                  </Text>
                )}
              </Group>

              {/* Outcome */}
              {activity.outcome && (
                <Paper p="xs" bg="gray.0" radius="sm">
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>Outcome</Text>
                  <Text size="sm" style={accessibilityStyles}>{activity.outcome}</Text>
                </Paper>
              )}

              {/* Attachments */}
              {activity.attachments && activity.attachments.length > 0 && (
                <Group gap="xs">
                  {activity.attachments.map((attachment, i) => (
                    <Badge
                      key={i}
                      size="xs"
                      variant="outline"
                      leftSection={<IconFile size={10} />}
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          component="a"
                          href={attachment.url}
                          target="_blank"
                        >
                          <IconExternalLink size={10} />
                        </ActionIcon>
                      }
                    >
                      {attachment.name}
                    </Badge>
                  ))}
                </Group>
              )}

              {/* User */}
              {activity.user && (
                <Group gap="xs">
                  <Avatar src={activity.user.avatar} size="xs" radius="xl">
                    {activity.user.name.charAt(0)}
                  </Avatar>
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>{activity.user.name}</Text>
                </Group>
              )}
            </Stack>
          </Paper>
        </Group>
      </SlideIn>
    );

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="md">
            {/* Header */}
            {onAddActivity && (
              <Group justify="space-between">
                <Text fw={600} style={accessibilityStyles}>Activity Timeline</Text>
                <Menu position="bottom-end">
                  <Menu.Target>
                    <Button variant="subtle" size="xs">
                      Log Activity
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconMail size={14} />}
                      onClick={() => onAddActivity('email')}
                    >
                      Email
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconPhone size={14} />}
                      onClick={() => onAddActivity('call')}
                    >
                      Call
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconCalendar size={14} />}
                      onClick={() => onAddActivity('meeting')}
                    >
                      Meeting
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconNote size={14} />}
                      onClick={() => onAddActivity('note')}
                    >
                      Note
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconCheck size={14} />}
                      onClick={() => onAddActivity('task')}
                    >
                      Task
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}

            {/* Activities */}
            {Object.entries(groupedActivities).map(([dateKey, dateActivities]) => (
              <Box key={dateKey}>
                {groupByDate && dateKey !== 'all' && (
                  <Text size="sm" fw={500} c="dimmed" mb="sm" style={accessibilityStyles}>
                    {formatDate(new Date(dateKey))}
                  </Text>
                )}
                <Stack gap="sm">
                  {dateActivities.map((activity, index) => (
                    <Box key={activity.id}>{renderActivity(activity, index)}</Box>
                  ))}
                </Stack>
              </Box>
            ))}

            {displayActivities.length === 0 && (
              <Box py="xl" ta="center">
                <Text c="dimmed" style={accessibilityStyles}>{emptyMessage}</Text>
              </Box>
            )}

            {/* Show more */}
            {maxItems && activities.length > maxItems && (
              <Text size="sm" c="dimmed" ta="center" style={accessibilityStyles}>
                Showing {maxItems} of {activities.length} activities
              </Text>
            )}
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

ActivityTimeline.displayName = 'ActivityTimeline';

export default ActivityTimeline;
